import { BASE_URL } from 'config';
import React, { useState, useEffect, useCallback } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { shippingPriceSchema } from "../../../schemas/shippingPricingSchema";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  Button,
  Typography,
  FormControl,
  FormHelperText,
  InputLabel,
  Autocomplete,
} from "@mui/material";
import axios from "axios";
import Swal from "sweetalert2";
import { useMaterialUIController } from "context";
import { useTheme } from "@mui/material/styles";
const AddShippingPricingForm = ({ onClose, onSuccess, initialData }) => {
  console.log("🚀 ~ AddShippingPricingForm ~ initialData:", initialData);

  const [controller] = useMaterialUIController();
  const darkMode = controller.darkMode;
  const theme = useTheme();

  const [shippingMethods, setShippingMethods] = useState([]);

  const [countries, setCountries] = useState([]);

  const [originRegions, setOriginRegions] = useState([]);
  const [originCities, setOriginCities] = useState([]);

  const [destinationRegions, setDestinationRegions] = useState([]);
  const [destinationCities, setDestinationCities] = useState([]);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(shippingPriceSchema),
    defaultValues: {
      shipping_method_id: "",
      origin_country_id: "",
      origin_region_id: "",
      origin_city_id: "",
      destination_country_id: "",
      destination_region_id: "",
      destination_city_id: "",
      weight: "",
      height: "",
      width: "",
      depth: "",
      price: "",
    },
  });

  // Watchers for dependency fetching
  const watchedOriginCountry = watch("origin_country_id");
  const watchedOriginRegion = watch("origin_region_id");
  const watchedDestinationCountry = watch("destination_country_id");
  const watchedDestinationRegion = watch("destination_region_id");

  // Optimize fetching with useCallback
  const fetchData = useCallback(async (endpoint, setter) => {
    const token = localStorage.getItem("user-token");
    try {
      const response = await fetch(`${BASE_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setter(data.data || []);
    } catch (error) {
      console.error(`Error fetching ${endpoint}:`, error);
      setter([]);
    }
  }, []);

  // Reset form when initialData changes
  useEffect(() => {
    if (initialData) {
      reset({
        shipping_method_id: initialData.shipping_method_id || "",
        origin_country_id: initialData.origin_region?.parent_id || "",
        origin_region_id: initialData.origin_region?.id || "",
        origin_city_id: initialData.origin_city?.id || "",
        destination_country_id: initialData.destination_region?.parent_id || "",
        destination_region_id: initialData.destination_region?.id || "",
        destination_city_id: initialData.destination_city?.id || "",
        weight: initialData.weight || "",
        height: initialData.height || "",
        width: initialData.width || "",
        depth: initialData.depth || "",
        price: initialData.price || "",
        id: initialData.id,
      });
    } else {
      reset({
        shipping_method_id: "",
        origin_country_id: "",
        origin_region_id: "",
        origin_city_id: "",
        destination_country_id: "",
        destination_region_id: "",
        destination_city_id: "",
        weight: "",
        height: "",
        width: "",
        depth: "",
        price: "",
      });
    }
  }, [initialData, reset]);

  // Initial load: Shipping Methods & Countries
  useEffect(() => {
    fetchData("/administrator/shipping-methods", setShippingMethods);
    fetchData("/administrator/locations/countries", setCountries);
  }, [fetchData]);

  // Fetch Origin Regions
  useEffect(() => {
    if (watchedOriginCountry) {
      fetchData(`/administrator/locations/children/${watchedOriginCountry}`, setOriginRegions);
    } else {
      setOriginRegions([]);
    }
    setOriginCities([]);
  }, [watchedOriginCountry, fetchData]);

  // Fetch Origin Cities
  useEffect(() => {
    if (watchedOriginRegion) {
      fetchData(`/administrator/locations/children/${watchedOriginRegion}`, setOriginCities);
    } else {
      setOriginCities([]);
    }
  }, [watchedOriginRegion, fetchData]);

  // Fetch Destination Regions
  useEffect(() => {
    if (watchedDestinationCountry) {
      fetchData(`/administrator/locations/children/${watchedDestinationCountry}`, setDestinationRegions);
    } else {
      setDestinationRegions([]);
    }
    setDestinationCities([]);
  }, [watchedDestinationCountry, fetchData]);

  // Fetch Destination Cities
  useEffect(() => {
    if (watchedDestinationRegion) {
      fetchData(`/administrator/locations/children/${watchedDestinationRegion}`, setDestinationCities);
    } else {
      setDestinationCities([]);
    }
  }, [watchedDestinationRegion, fetchData]);

  // handle submit
  const onSubmit = async (data) => {
    try {
      Swal.fire({
        title: "جاري الحفظ...",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const formPayload = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (value !== null && value !== undefined && key !== "id") {
          formPayload.append(key, value);
        }
      });
      console.log("Submitting Payload", Object.fromEntries(formPayload));

      const token = localStorage.getItem("user-token");
      const endpoint = initialData?.id
        ? `${BASE_URL}/administrator/shipping-rates/update/${initialData.id}`
        : BASE_URL + "/administrator/shipping-rates/store";

      const response = await axios.post(endpoint, formPayload, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` },
      });

      Swal.close();

      if (response.data.status || response.data.success) { // Handle potential API response variations
        Swal.fire({ icon: "success", title: "تم الحفظ", confirmButtonText: "موافق" });
        onSuccess(response.data); // Pass data back if needed
        onClose();
      } else {
        throw new Error(response.data.message || "حدث خطأ");
      }
    } catch (error) {
      Swal.close();
      console.error("Submission error:", error);
      Swal.fire({ icon: "error", title: "خطأ", text: error.message || "حدث خطأ أثناء الحفظ" });
    }
  };

  return (
    <Box
      component="form"
      noValidate
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        p: 3,
        maxWidth: 600,
        mx: "auto",
        bgcolor: darkMode ? theme.palette.background.default : "background.paper",
        borderRadius: 3,
        boxShadow: 3,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Typography variant="h5" align="center" gutterBottom>
        إضافة سعر شحن جديد
      </Typography>

      {/* شركة الشحن */}
      <Controller
        name="shipping_method_id"
        control={control}
        render={({ field }) => (
          <FormControl fullWidth error={!!errors.shipping_method_id}>
            <InputLabel id="shipping-method-label">شركة الشحن</InputLabel>
            <Select
              {...field}
              labelId="shipping-method-label"
              label="شركة الشحن"
              value={field.value || ""} // Ensure controlled
              sx={{ textAlign: "right", p: 2 }}
            >
              {shippingMethods.map((sm) => (
                <MenuItem key={sm.id} value={sm.id}>
                  {sm.name}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{errors.shipping_method_id?.message}</FormHelperText>
          </FormControl>
        )}
      />

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        {/* منشأ */}
        <Box sx={{ flex: "1 1 30%", mb: 1 }}>
          <Controller
            name="origin_country_id"
            control={control}
            render={({ field }) => (
              <Autocomplete
                options={countries}
                getOptionLabel={(option) => option.name || ""}
                value={countries.find((c) => c.id === field.value) || null}
                onChange={(_, newValue) => {
                  field.onChange(newValue?.id);
                  setValue("origin_region_id", "");
                  setValue("origin_city_id", "");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="دولة المنشأ"
                    error={!!errors.origin_country_id}
                    helperText={errors.origin_country_id?.message}
                  />
                )}
              />
            )}
          />
        </Box>
        <Box sx={{ flex: "1 1 30%", mb: 1 }}>
          <Controller
            name="origin_region_id"
            control={control}
            render={({ field }) => (
              <Autocomplete
                options={originRegions}
                getOptionLabel={(option) => option.name || ""}
                value={originRegions.find((r) => r.id === field.value) || null}
                onChange={(_, newValue) => {
                  field.onChange(newValue?.id);
                  setValue("origin_city_id", "");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="منطقة المنشأ"
                    error={!!errors.origin_region_id}
                    helperText={errors.origin_region_id?.message}
                  />
                )}
              />
            )}
          />
        </Box>
        <Box sx={{ flex: "1 1 30%", mb: 1 }}>
          <Controller
            name="origin_city_id"
            control={control}
            render={({ field }) => (
              <Autocomplete
                options={originCities}
                getOptionLabel={(option) => option.name || ""}
                value={originCities.find((c) => c.id === field.value) || null}
                onChange={(_, newValue) => field.onChange(newValue?.id)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="محافظة المنشأ"
                    error={!!errors.origin_city_id}
                    helperText={errors.origin_city_id?.message}
                  />
                )}
              />
            )}
          />
        </Box>

        {/* الوجهة */}
        <Box sx={{ flex: "1 1 30%", mb: 1 }}>
          <Controller
            name="destination_country_id"
            control={control}
            render={({ field }) => (
              <Autocomplete
                options={countries}
                getOptionLabel={(option) => option.name || ""}
                value={countries.find((c) => c.id === field.value) || null}
                onChange={(_, newValue) => {
                  field.onChange(newValue?.id);
                  setValue("destination_region_id", "");
                  setValue("destination_city_id", "");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="دولة الوجهة"
                    error={!!errors.destination_country_id}
                    helperText={errors.destination_country_id?.message}
                  />
                )}
              />
            )}
          />
        </Box>
        <Box sx={{ flex: "1 1 30%", mb: 1 }}>
          <Controller
            name="destination_region_id"
            control={control}
            render={({ field }) => (
              <Autocomplete
                options={destinationRegions}
                getOptionLabel={(option) => option.name || ""}
                value={destinationRegions.find((r) => r.id === field.value) || null}
                onChange={(_, newValue) => {
                  field.onChange(newValue?.id);
                  setValue("destination_city_id", "");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="منطقة الوجهة"
                    error={!!errors.destination_region_id}
                    helperText={errors.destination_region_id?.message}
                  />
                )}
              />
            )}
          />
        </Box>
        <Box sx={{ flex: "1 1 30%", mb: 1 }}>
          <Controller
            name="destination_city_id"
            control={control}
            render={({ field }) => (
              <Autocomplete
                options={destinationCities}
                getOptionLabel={(option) => option.name || ""}
                value={destinationCities.find((c) => c.id === field.value) || null}
                onChange={(_, newValue) => field.onChange(newValue?.id)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="محافظة الوجهة"
                    error={!!errors.destination_city_id}
                    helperText={errors.destination_city_id?.message}
                  />
                )}
              />
            )}
          />
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 2 }}>
        <Controller
          name="weight"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="وزن المنتج (كجم)"
              type="number"
              required // Optional for H5 validation, but good for UI
              inputProps={{ step: 1, min: 0 }}
              fullWidth
              value={field.value || ""}
              error={!!errors.weight}
              helperText={errors.weight?.message}
              sx={{ mb: 3 }}
            />
          )}
        />
        <Controller
          name="height"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="الارتفاع (سم)"
              type="number"
              required
              inputProps={{ step: 1, min: 0 }}
              fullWidth
              value={field.value || ""}
              error={!!errors.height}
              helperText={errors.height?.message}
            />
          )}
        />
        <Controller
          name="width"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="العرض (سم)"
              type="number"
              required
              inputProps={{ step: 1, min: 0 }}
              fullWidth
              value={field.value || ""}
              error={!!errors.width}
              helperText={errors.width?.message}
            />
          )}
        />
        <Controller
          name="depth"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="العمق (سم)"
              type="number"
              required
              inputProps={{ step: 1, min: 0 }}
              fullWidth
              value={field.value || ""}
              error={!!errors.depth}
              helperText={errors.depth?.message}
            />
          )}
        />
      </Box>

      {/* السعر */}
      <Controller
        name="price"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label="سعر الشحن"
            type="number"
            inputProps={{ step: 1, min: 0 }}
            fullWidth
            value={field.value || ""}
            error={!!errors.price}
            helperText={errors.price?.message}
          />
        )}
      />

      {/* أزرار */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button
          variant="outlined"
          color="error"
          onClick={onClose}
          sx={{ color: "text.border", borderColor: "text.border" }}
        >
          إلغاء
        </Button>
        <Button
          variant="contained"
          color="primary"
          type="submit"
          sx={{
            backgroundColor: "primary.dark",
            "&:hover": { backgroundColor: "primary.main" },
          }}
        >
          حفظ
        </Button>
      </Box>
    </Box>
  );
};

export default AddShippingPricingForm;
